/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, OnInit, signal} from '@angular/core';
import {completeCopyModal} from '../common/util/complete-copy-modal';
import {finalize, Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import {nextCopyTitle} from '../../components/util/next-copy-title';
import {Uuid} from '../../components/util/uuid';
import {ExternalAdUnitApiService, ExternalAdUnitModel} from './external-ad-unit-api.service';
import {FormsModule} from '@angular/forms';
import {APP_SHARED} from '../common/app-shared';

export interface ExternalAdUnitCopyResult {
  model: ExternalAdUnitModel;
  destination: boolean;
}

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule],
  selector: 'adn-external-ad-unit-copy-modal',
  template: `
    <form class="form-horizontal" (ngSubmit)="copy()">
      <div class="modal-header dialogAddStuff">
        <button type="button" class="close" (click)="dismiss()">&times;</button>
        <h4 class="modal-title">
          <span>{{ 'label.copy.externalAdUnit' | ogTranslate }}</span>
          <small>{{ originalName }}</small>
        </h4>
      </div>
      <div class="modal-body">
        @if (copyFailed()) {
          <p class="alert alert-danger">The external ad unit could not be copied. Try again.</p>
        }
        <div class="form-group">
          <label class="col-md-3 control-label">{{ 'label.name' | ogTranslate }}</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="externalAdUnit.name" [(ngModel)]="name" required>
          </div>
        </div>

        <div class="form-group">
          <label class="control-label col-md-3">External Site ID</label>
          <div class="col-md-9">
            <input class="form-control" name="externalAdUnit.externalSiteId" [(ngModel)]="externalSiteId">
          </div>
        </div>

        <div class="form-group">
          <label class="control-label col-md-3">External Ad Unit ID</label>
          <div class="col-md-9">
            <input class="form-control" name="externalAdUnit.externalAdUnitId" [(ngModel)]="externalAdUnitId" required>
          </div>
        </div>

        <div class="form-group">
          <label class="control-label col-md-3">External Ad Type</label>
          <div class="col-md-9">
            <select class="form-control" name="externalAdUnit.externalAdType" [(ngModel)]="externalAdType">
              <option [ngValue]="null"></option>
              <option *ngFor="let adType of externalAdTypes" [ngValue]="adType">
                {{ ('externalAdUnit.adType.' + adType) | ogTranslate }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label class="control-label col-md-3">Dimensions<br>(Width &times; Height)</label>
          <div class="col-md-9">
            <div class="formControlRelated" *ngFor="let dims of dimensions; let i = index; let first = first; let last = last">
              <input type="number" min="0" class="form-control unitDimensions" [name]="'dims' + i + '.width'" [attr.name]="'dims' + i + '.width'" [(ngModel)]="dims[0]" required>
              &times;
              <input type="number" min="0" class="form-control unitDimensions" [name]="'dims' + i + '.height'" [attr.name]="'dims' + i + '.height'" [(ngModel)]="dims[1]" required>
              <button type="button" title="Remove these dimensions" class="btn btn-default" (click)="removeDimensions(i)" [disabled]="first && dimensions.length < 2">
                <span class="fa fa-minus"></span>Remove
              </button>
              <button type="button" title="Add more dimensions" class="btn btn-default" (click)="addDimensions()" [disabled]="!last">
                <span class="fa fa-plus"></span>Add
              </button>
            </div>
          </div>
        </div>

        <div class="checkbox text-right">
          <label class="checkbox">
            <input type="checkbox" [(ngModel)]="destination" name="externalAdUnit.destination">
            <span class="small">Go to new ad unit after copy</span>
          </label>
        </div>
      </div>
      <div class="modal-footer">
        <a class="btn btn-link btn-xs" (click)="dismiss()">{{ 'button.cancel' | ogTranslate }}</a>
        <button type="submit" class="btn btn-default" [disabled]="pending()">{{ 'label.copy' | ogTranslate }}</button>
      </div>
    </form>
  `
})
export class ExternalAdUnitCopyModalComponent implements OnInit {
  model: ExternalAdUnitModel;

  readonly onCopied = new Subject<ExternalAdUnitCopyResult>();
  readonly externalAdTypes = ['HTML', 'TEXT', 'VIDEO', 'FLASH', 'IMAGE', 'MIXED'];

  originalName = '';
  name = '';
  externalSiteId = '';
  externalAdUnitId = '';
  externalAdType: string | null = null;
  dimensions: any[] = [[]];
  destination = true;
  readonly pending = signal(false);
  readonly copyFailed = signal(false);

  constructor(
    public bsModalRef: BsModalRef,
    private readonly api: ExternalAdUnitApiService
  ) {
  }

  ngOnInit(): void {
    this.originalName = this.model?.name || '';
    this.name = nextCopyTitle(this.model?.name || '');
    this.externalSiteId = this.model?.externalSiteId || '';
    this.externalAdUnitId = this.model?.externalAdUnitId || '';
    this.externalAdType = this.model?.externalAdType || null;
    this.dimensions = _.cloneDeep(_.isArray(this.model?.dimensions) && this.model.dimensions.length ? this.model.dimensions : [[]]);
  }

  addDimensions(): void {
    this.dimensions = this.dimensions || [];
    this.dimensions.push([]);
  }

  removeDimensions(index: number): void {
    _.pullAt(this.dimensions, index);
  }

  copy(): void {
    if (this.pending()) {
      return;
    }
    const copy: ExternalAdUnitModel = _.cloneDeep({...this.model,
      id: Uuid.generate(),
      name: this.name,
      externalSiteId: this.externalSiteId,
      externalAdUnitId: this.externalAdUnitId,
      externalAdType: this.externalAdType,
      dimensions: this.dimensions
    });
    delete copy.width;
    delete copy.height;
    this.pending.set(true);
    this.copyFailed.set(false);
    this.api.save(copy).pipe(
      finalize(() => this.pending.set(false))
    ).subscribe({
      next: (saved) => {
        completeCopyModal(this.bsModalRef, this.onCopied, {model: saved, destination: this.destination});
      },
      error: () => this.copyFailed.set(true)
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
